import { useEffect, useMemo, useRef, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import {
  DEVICE_LIMIT,
  deviceSwapExhaustedCopy,
  deviceListLabel,
  spokenDeviceName,
  type ActivatedDevice
} from "@recital-master/shared";

import { useAuthState } from "@/auth/AuthProvider";
import { Backdrop } from "@/components/Artwork";
import {
  DeviceRow,
  deviceDateLabel,
  type DeviceRowData
} from "@/components/DeviceRow";
import { useAccount } from "@/data/AccountProvider";
import {
  deviceSwapBalanceCopy
} from "@/devices/deviceSwapCopy";
import { Muted, Overline, Text } from "@/design/Text";
import { palette, space } from "@/design/tokens";
import { firebaseErrorCode, removeDevice } from "@/firebase/callables";
import { useAppRouter } from "@/lib/appRouter";

type PreviewState = "pending" | "quota" | null;

const PREVIEW_DEVICES: ActivatedDevice[] = [
  {
    deviceId: "preview-mac",
    platform: "web",
    formFactor: "computer",
    operatingSystem: "macOS",
    modelCode: null,
    manufacturer: null,
    browserName: "Chrome",
    displayName: null,
    locationLabel: "Winnipeg, MB",
    activatedAt: "2026-07-04T12:00:00.000Z",
    lastUsedAt: "2026-07-14T12:00:00.000Z",
    removedAt: null
  },
  {
    deviceId: "preview-phone",
    platform: "ios",
    formFactor: "phone",
    operatingSystem: "iOS",
    modelCode: "iPhone 6s",
    manufacturer: "Apple",
    browserName: null,
    displayName: "iPhone 6s",
    locationLabel: "Winnipeg, MB",
    activatedAt: "2026-06-20T12:00:00.000Z",
    lastUsedAt: "2026-07-12T12:00:00.000Z",
    removedAt: null
  },
  {
    deviceId: "preview-tablet",
    platform: "ios",
    formFactor: "tablet",
    operatingSystem: "iPadOS",
    modelCode: "iPad Pro (11-inch)",
    manufacturer: "Apple",
    browserName: null,
    displayName: "iPad Pro (11-inch)",
    locationLabel: "Brandon, MB",
    activatedAt: "2026-06-14T12:00:00.000Z",
    lastUsedAt: "2026-07-08T12:00:00.000Z",
    removedAt: null
  }
];

function rowData(device: ActivatedDevice): DeviceRowData {
  return {
    deviceId: device.deviceId,
    label: deviceListLabel(device),
    platform: device.operatingSystem,
    formFactor: device.formFactor,
    locationLabel: device.locationLabel,
    lastUsed: deviceDateLabel(device.lastUsedAt)
  };
}

export function DeviceSwapPanel({
  artworkUri,
  fallbackSeed,
  framed,
  blocked,
  forcePreview = false,
  previewState = null,
  onPreviewResolve
}: {
  artworkUri: string | null;
  fallbackSeed: string;
  framed: boolean;
  blocked: boolean;
  forcePreview?: boolean;
  previewState?: PreviewState;
  onPreviewResolve?: () => void;
}) {
  const router = useAppRouter();
  const {
    device: clientDevice,
    deviceLimitReached,
    nextSwapAvailableAt,
    swapsRemaining,
    ensureActiveDevice
  } = useAuthState();
  const { devices: liveDevices } = useAccount();
  const devices = forcePreview ? PREVIEW_DEVICES : liveDevices;
  const [pendingDeviceId, setPendingDeviceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quotaErrorAfterPress, setQuotaErrorAfterPress] = useState<string | null>(null);
  const [accountLinkHighlighted, setAccountLinkHighlighted] = useState(false);
  const claimInFlight = useRef(false);
  const lastAutoClaimKey = useRef<string | null>(null);
  const rows = useMemo(() => devices.map(rowData), [devices]);
  const shownPendingId = previewState === "pending" ? rows[0]?.deviceId ?? null : pendingDeviceId;
  const quotaSettled =
    previewState === "quota" ||
    (!forcePreview &&
      ((deviceLimitReached && swapsRemaining === 0) || quotaErrorAfterPress != null));
  const settledQuotaMessage =
    quotaErrorAfterPress ?? deviceSwapExhaustedCopy(nextSwapAvailableAt);
  const shownError =
    quotaSettled
      ? settledQuotaMessage
      : error;
  const clientSpokenName = spokenDeviceName(
    clientDevice ?? (forcePreview ? PREVIEW_DEVICES[0] : null)
  );
  const title = `This ${clientSpokenName} needs a spot`;
  const autoClaimKey = devices.map((device) => device.deviceId).sort().join(":");

  useEffect(() => {
    if (
      forcePreview ||
      !blocked ||
      !deviceLimitReached ||
      devices.length >= DEVICE_LIMIT ||
      claimInFlight.current ||
      lastAutoClaimKey.current === autoClaimKey
    ) {
      return;
    }
    lastAutoClaimKey.current = autoClaimKey;
    claimInFlight.current = true;
    void ensureActiveDevice().finally(() => {
      claimInFlight.current = false;
    });
  }, [autoClaimKey, blocked, deviceLimitReached, devices.length, ensureActiveDevice, forcePreview]);

  async function swap(deviceId: string) {
    if (claimInFlight.current || pendingDeviceId || quotaSettled) return;
    if (forcePreview) {
      // Dev QA rehearses the real choreography: pending beat, then resolve.
      setPendingDeviceId(deviceId);
      setTimeout(() => {
        setPendingDeviceId(null);
        onPreviewResolve?.();
      }, 1200);
      return;
    }
    claimInFlight.current = true;
    setPendingDeviceId(deviceId);
    setError(null);
    try {
      await removeDevice(deviceId);
      const active = await ensureActiveDevice({ throwOnError: true });
      if (!active) throw new Error("device-activation-failed");
    } catch (swapError) {
      const quotaExhausted = firebaseErrorCode(swapError) === "resource-exhausted";
      const quotaMessage = deviceSwapExhaustedCopy(nextSwapAvailableAt);
      if (quotaExhausted) setQuotaErrorAfterPress(quotaMessage);
      setError(
        quotaExhausted
          ? quotaMessage
          : "Couldn't swap that device. Try another or manage devices in settings."
      );
    } finally {
      claimInFlight.current = false;
      setPendingDeviceId(null);
    }
  }

  return (
    <View style={[styles.panel, !framed && styles.panelNarrow]}>
      {framed ? (
        <Backdrop uri={artworkUri} fallbackSeed={fallbackSeed} intensity={1} />
      ) : null}
      {framed ? <View style={[styles.scrim, { pointerEvents: "none" }]} /> : null}
      <View style={[styles.body, framed ? styles.bodyWide : styles.bodyNarrow]}>
        <View style={styles.content}>
          <View style={styles.heading}>
            <Overline color={palette.textSoft}>
              {DEVICE_LIMIT} OF {DEVICE_LIMIT} DEVICES
            </Overline>
            <Text variant="display" style={styles.title}>
              {title}
            </Text>
            <Muted variant="small" style={styles.subtitle}>
              Your family plan covers {DEVICE_LIMIT} devices. Swap one out to start watching here.
            </Muted>
          </View>

          <View style={styles.devices}>
            {rows.map((device) => (
              <DeviceRow
                key={device.deviceId}
                device={device}
                compact
                actionLabel="Swap"
                pending={shownPendingId === device.deviceId}
                actionDisabled={
                  quotaSettled || (shownPendingId != null && shownPendingId !== device.deviceId)
                }
                onAction={() => void swap(device.deviceId)}
              />
            ))}
          </View>

          <View style={styles.footer}>
            {shownError ? (
              <Text variant="small" style={[styles.footnote, styles.error]}>
                {shownError}
              </Text>
            ) : (
              <Muted variant="small" style={styles.footnote}>
                {deviceSwapBalanceCopy(swapsRemaining)}
              </Muted>
            )}
            <Pressable
              accessibilityRole="link"
              hitSlop={space.sm}
              onBlur={() => setAccountLinkHighlighted(false)}
              onFocus={() => setAccountLinkHighlighted(true)}
              onHoverIn={() => setAccountLinkHighlighted(true)}
              onHoverOut={() => setAccountLinkHighlighted(false)}
              onPress={() => router.push("/account")}
              style={styles.accountLink}
            >
              <Text
                variant="small"
                color={accountLinkHighlighted ? palette.textSoft : palette.muted}
                style={accountLinkHighlighted && styles.accountLinkHighlighted}
              >
                Manage devices
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    backgroundColor: palette.bg,
    flexGrow: 1,
    overflow: "hidden",
    width: "100%"
  },
  panelNarrow: {
    backgroundColor: "transparent"
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(8, 9, 14, 0.82)"
  },
  body: {
    flexGrow: 1,
    justifyContent: "center"
  },
  bodyNarrow: {
    padding: space.lg
  },
  bodyWide: {
    paddingHorizontal: space.xl,
    paddingVertical: space.xl
  },
  content: {
    alignSelf: "center",
    gap: space.md,
    maxWidth: 680,
    width: "100%"
  },
  heading: {
    alignItems: "center",
    gap: space.xs
  },
  title: {
    textAlign: "center"
  },
  subtitle: {
    maxWidth: 480,
    textAlign: "center"
  },
  devices: {
    gap: space.sm
  },
  error: {
    color: palette.danger,
    textAlign: "center"
  },
  footer: {
    alignItems: "center",
    gap: space.xs,
    marginTop: space.xs
  },
  footnote: {
    color: palette.faint,
    textAlign: "center"
  },
  accountLink: {
    flexShrink: 0
  },
  accountLinkHighlighted: {
    textDecorationLine: "underline"
  }
});
